import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import invariant from 'tiny-invariant';

import type { DraftInvoice, SubmittedInvoice } from '~/api/invoices/types';
import type { NumberingSeries } from '~/types/user';

import { useClients } from '~/api/clients';
import { HttpBusinessConflictError } from '~/api/errors';
import { usePreviewInvoice, useSubmitInvoice, useUpdateLockedInvoice } from '~/api/invoices';
import { useUser } from '~/api/user';
import { AlertDialog, Blocker, Button, Container, Dialog, FileDropzone, Header, Icon, Menu, Separator, UploadedFile } from '~/components';
import { Field, Form, Textarea } from '~/components/Form';
import { env } from '~/constants/env';
import { bypassBlocker } from '~/constants/router';
import { CompleteAccountDialog } from '~/features/account';
import { AutosaveStatus, CopyrightSection, FormLines, LinesTotal } from '~/features/documents';
import { useDialog } from '~/hooks/useDialog';
import { useIsFirstRender } from '~/hooks/useIsFirstRender';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { statusColorThemes } from '~/styles/themes.css';
import { clsx } from '~/styles/utils';
import { toast } from '~/utils/toast';
import { getVatExemptLimitStatus, isAccountCompleted } from '~/utils/user';

import type { InvoiceConfirmationData } from '../components/dialogs.types';
import type { CreateEditInvoiceFormType } from './types';

import { InvoiceConfirmationDialog } from '../components/InvoiceConfirmationDialog';
import { InvoicePreviewDialog } from '../components/InvoicePreviewDialog';
import { VatExemptLimitDialog } from '../components/VatExemptLimitDialog';
import { mapInvoiceFormDataToDraftPayload, mapInvoiceFormDataToPayload } from '../invoices.utils';
import {
  footerSectionStyle,
  linesSectionStyle,
  minCommissionWarningStyle,
  peppolNoticeStyle,
  sectionsStyle,
} from './CreateEditInvoiceForm.css';
import { useAttachment, useAutoSave, useCalculationData, useInvoiceDefaultValues } from './hooks';
import { InvoiceFormCommandMenuItems } from './InvoiceFormCommandMenuItems';
import { InvoiceBasicSection } from './sections/InvoiceBasicSection';

type Props =
  | {
      invoice: DraftInvoice;
      mode: 'create';
    }
  | {
      invoice: SubmittedInvoice;
      mode: 'edit';
    };

export const CreateEditInvoiceForm = ({ invoice, mode }: Props) => {
  const { data: user } = useUser();
  const { data: clients } = useClients();
  const previewMutation = usePreviewInvoice(invoice.id);
  const submitInvoiceMutation = useSubmitInvoice(invoice.id);
  const updateLockedInvoiceMutation = useUpdateLockedInvoice(invoice.id);

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'invoices', 'settings', 'validation']);

  const confirmationDialogHandle = Dialog.useHandle<{ isMinimumCommissionApplied: boolean; numberingSeries: NumberingSeries | null }>();
  const totalCoTooLowDialogHandle = AlertDialog.useHandle();
  const saveAndExitDialogHandle = AlertDialog.useHandle();

  // These two stay controlled: onSubmit awaits their outcome before it continues.
  const completeAccountDialog = useDialog<void, boolean>();
  const vatExemptLimitDialog = useDialog<void, boolean>();
  const attachment = useAttachment(invoice.id);

  const defaultValues = useInvoiceDefaultValues(invoice);
  const form = useForm<CreateEditInvoiceFormType>({ defaultValues });
  const {
    control,
    formState: { isDirty },
    getValues,
    handleSubmit,
    watch,
  } = form;
  const watchClientId = watch('clientId');
  const selectedClient = useMemo(() => clients.find(({ id }) => id === watchClientId) ?? null, [clients, watchClientId]);

  const isFirstRender = useIsFirstRender();
  const isAutoSaving = useAutoSave(invoice.id, control, mode === 'create' && !submitInvoiceMutation.isPending && !isFirstRender);
  const { calculationData, isFetching: isCalculationDataFetching } = useCalculationData(invoice, control);

  // Computed here (not just inside onSubmit) so the VAT-exempt-limit dialog's own dismiss
  // handling below can tell whether the current status is the hard "overLimit" block.
  const vatExemptLimitStatus = getVatExemptLimitStatus(user);

  const onSubmit = handleSubmit(async (data) => {
    // Check account completion
    if (!isAccountCompleted(user) && !(await completeAccountDialog.open())) return;

    // Check VAT exempt revenue limit
    const showVatExemptLimitDialog =
      vatExemptLimitStatus === 'approachingLimit' || vatExemptLimitStatus === 'nearLimit' || vatExemptLimitStatus === 'overLimit';

    if (showVatExemptLimitDialog && !(await vatExemptLimitDialog.open())) return;

    // Check CO below minimum commisison, meaning FC would be €0
    if (calculationData.fcExclVat === 0) return totalCoTooLowDialogHandle.open(null);

    // Ask confirmation
    confirmationDialogHandle.openWithPayload({
      numberingSeries: user.numberingSeries.find((numberingSeries) => numberingSeries.id === data.numberingSeriesId) ?? null,
      isMinimumCommissionApplied: calculationData.isMinimumCommissionApplied,
    });
  });

  const onConfirm = ({ approvalType, fcEntryNumber, nextNumber }: InvoiceConfirmationData) => {
    const data = getValues();

    // Sanity check of required fields
    if (mode === 'create') {
      invariant(fcEntryNumber !== null, 'Expected FC entry number to be defined when submitting draft invoice.');
      invariant(nextNumber !== null, 'Expected next number to be defined when submitting draft invoice.');
    }

    submitInvoiceMutation.mutate(
      {
        ...mapInvoiceFormDataToPayload(data),
        fcEntryNumber,
        nextNumber,
        approvalType,
      },
      {
        onSuccess: () => {
          toast.success({ description: t(`invoices:alerts.success${mode === 'create' ? 'Created' : 'Updated'}`) });
          navigate(r(routes.showInvoice, { invoiceId: invoice.id }), { state: bypassBlocker, replace: true });
        },
        onError: (error, { fcEntryNumber }) =>
          toast.danger({
            description:
              error instanceof HttpBusinessConflictError && error.errorCode === 'InvoiceEntryNumberMustBeUnique'
                ? t('invoices:alerts.errorDuplicateEntryNumber', { entryNumber: fcEntryNumber })
                : t('common:error'),
          }),
      },
    );
  };

  const onSaveAndExit = () => {
    const data = getValues();

    updateLockedInvoiceMutation.mutate(mapInvoiceFormDataToPayload(data), {
      onSuccess: () => navigate(r(routes.showInvoice, { invoiceId: invoice.id }), { state: bypassBlocker, replace: true }),
      onError: () => toast.danger({ description: t('common:error') }),
    });
  };

  const backroute = mode === 'create' ? routes.invoices : r(routes.showInvoice, { invoiceId: invoice.id });

  return (
    <>
      <Header.Root>
        <Header.Content backTo={backroute} title={t(`invoices:createEdit.title.${mode}`)} />

        <Header.Actions>
          {mode === 'create' && <AutosaveStatus lastSavedAt={invoice.updatedAt} status={isAutoSaving ? 'saving' : 'saved'} />}

          <Header.Action
            emphasis="secondary"
            icon="Visibility"
            loading={previewMutation.isPending}
            onClick={() =>
              previewMutation.mutate(mapInvoiceFormDataToDraftPayload(getValues()), {
                onError: () => toast.danger({ description: t('common:error') }),
              })
            }
          >
            {t('invoices:createEdit.preview')}
          </Header.Action>

          {mode === 'create' ? (
            <Header.Action icon="Send" onClick={onSubmit}>
              {t('common:actions.send')}
            </Header.Action>
          ) : (
            <Header.ActionGroup floatingIcon="Send">
              <Header.Action icon="Send" onClick={onSubmit}>
                {t('common:actions.send')}
              </Header.Action>

              <Header.ActionGroupMenu>
                <AlertDialog.Trigger
                  handle={saveAndExitDialogHandle}
                  render={<Menu.Item icon="Save">{t('invoices:createEdit.submit.saveAndExit')}</Menu.Item>}
                />
              </Header.ActionGroupMenu>
            </Header.ActionGroup>
          )}
        </Header.Actions>
      </Header.Root>

      <Blocker
        isBlocked={mode === 'edit'}
        leaveText={t(isDirty ? 'invoices:createEdit.blocker.leaveUnsaved' : 'invoices:createEdit.blocker.leave')}
        message={t(isDirty ? 'invoices:createEdit.blocker.messageUnsaved' : 'invoices:createEdit.blocker.message')}
      />

      <Container>
        <Form autoComplete="off" onSubmit={onSubmit}>
          {/* Basic info & copyright section */}
          <div className={sectionsStyle}>
            <InvoiceBasicSection fcEntryNumber={invoice.fcEntryNumber} form={form} mode={mode} />

            <Separator />

            <CopyrightSection form={form} />

            <Separator />

            {/* Invoice lines */}
            <div className={linesSectionStyle}>
              <FormLines form={form} i18nNamespace="invoices" />

              <LinesTotal calculationData={calculationData} form={form} isLoading={isCalculationDataFetching} />
            </div>

            <Separator />

            <div className={footerSectionStyle}>
              {/* Attachment */}
              <Field.Root>
                <Field.Label optional>{t('invoices:createEdit.attachments.attachment')}</Field.Label>

                {attachment.file ? (
                  <UploadedFile
                    error={attachment.file.error}
                    fileSize={attachment.file.size}
                    name={attachment.file.name}
                    onRemove={attachment.remove}
                    onRetry={attachment.retry}
                    onView={attachment.open}
                    // The endpoint does not stream progress, so the upload can only be shown as indeterminate.
                    progress={attachment.file.isUploading ? null : undefined}
                    removing={attachment.isRemoving}
                  />
                ) : (
                  <FileDropzone
                    accept={['.pdf']}
                    // Matches the API cap: the client email carries the attachment twice (as a file and inside the CO XML) and must stay below 10 MB.
                    maxFileSizeInMegaByte={3}
                    onSelect={attachment.select}
                  />
                )}
              </Field.Root>

              {/* User comments */}
              <Controller
                control={control}
                name="userComments"
                render={({ field: { name, onBlur, onChange, ref, value } }) => (
                  <Field.Root name={name}>
                    <Field.Label optional>{t('invoices:fields.remark.label')}</Field.Label>

                    <Textarea
                      onBlur={onBlur}
                      onValueChange={onChange}
                      placeholder={t('invoices:fields.remark.placeholder')}
                      ref={ref}
                      value={value}
                    />
                  </Field.Root>
                )}
              />
            </div>
          </div>
        </Form>
      </Container>

      {/* Confirmation dialog */}
      <Dialog.Root handle={confirmationDialogHandle}>
        {({ payload }) =>
          payload && (
            <InvoiceConfirmationDialog
              allowsAutomaticApproval={invoice.allowsAutomaticApproval}
              disabled={submitInvoiceMutation.isPending || updateLockedInvoiceMutation.isPending}
              isEdit={mode === 'edit'}
              numberingSeries={payload.numberingSeries}
              onConfirm={onConfirm}
              onSaveAndExit={onSaveAndExit}
            >
              <p>
                {invoice.allowsAutomaticApproval
                  ? t('invoices:createEdit.automaticApprovalModal.description')
                  : mode === 'create'
                    ? t('invoices:createEdit.confirmation.description')
                    : t('invoices:createEdit.confirmation.descriptionEdit')}
              </p>

              {payload.isMinimumCommissionApplied && (
                <small className={clsx(minCommissionWarningStyle, statusColorThemes.warning)}>
                  {t('invoices:createEdit.confirmation.minCommission')}
                </small>
              )}

              {!!selectedClient?.peppolId && (
                <p className={clsx(peppolNoticeStyle, statusColorThemes.info)}>
                  <Icon name="Peppol" size={16} />
                  {invoice.allowsAutomaticApproval
                    ? t('invoices:createEdit.confirmation.sentWithPeppolInstant')
                    : t('invoices:createEdit.confirmation.sentWithPeppol')}
                </p>
              )}
            </InvoiceConfirmationDialog>
          )
        }
      </Dialog.Root>

      {/* Preview dialog */}
      <InvoicePreviewDialog html={previewMutation.data} onClose={() => previewMutation.reset()} title={getValues('title') || null} />

      {/* Complete account dialog */}
      {completeAccountDialog.isOpen && (
        <CompleteAccountDialog onClose={() => completeAccountDialog.close(false)} onComplete={() => completeAccountDialog.close(true)} />
      )}

      {/* Vat exempt revenue limit dialogs */}
      <AlertDialog.Root
        onOpenChange={() => vatExemptLimitDialog.close(vatExemptLimitStatus !== 'overLimit')}
        open={vatExemptLimitDialog.isOpen}
      >
        {vatExemptLimitDialog.isOpen && <VatExemptLimitDialog onClose={(isBelowLimit) => vatExemptLimitDialog.close(isBelowLimit)} />}
      </AlertDialog.Root>

      {/* CO too low dialog */}
      <AlertDialog.Root handle={totalCoTooLowDialogHandle}>
        <AlertDialog.Body status="danger">
          <AlertDialog.Title>{t('invoices:createEdit.coTooLowModal.title')}</AlertDialog.Title>

          <AlertDialog.Description>{t('invoices:createEdit.coTooLowModal.description')}</AlertDialog.Description>

          <AlertDialog.Actions>
            <AlertDialog.Close render={<Button>{t('invoices:createEdit.coTooLowModal.dismiss')}</Button>} />
          </AlertDialog.Actions>
        </AlertDialog.Body>
      </AlertDialog.Root>

      {/* Saving keeps the invoice LockedByUser, so admins can't approve it until it is sent again. */}
      <AlertDialog.Root handle={saveAndExitDialogHandle}>
        <AlertDialog.Body status="warning">
          <AlertDialog.Title>{t('invoices:createEdit.saveAndExitDialog.title')}</AlertDialog.Title>

          <AlertDialog.Description>{t('invoices:createEdit.saveAndExitDialog.description')}</AlertDialog.Description>

          <AlertDialog.Actions>
            <AlertDialog.Close
              disabled={updateLockedInvoiceMutation.isPending}
              onClick={onSubmit}
              render={
                <Button emphasis="secondary" icon="Send">
                  {t('invoices:createEdit.saveAndExitDialog.send')}
                </Button>
              }
            />

            <Button icon="Save" loading={updateLockedInvoiceMutation.isPending} onClick={onSaveAndExit}>
              {t('invoices:createEdit.saveAndExitDialog.saveAndExit')}
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Body>
      </AlertDialog.Root>

      {(env.IS_LOCAL_DEV_ENV || env.IS_STAGING_ENV) && <InvoiceFormCommandMenuItems form={form} />}
    </>
  );
};
